// src/components/LoginForm.tsx

import React, { useState } from "react";
import { AuthAPI } from "../api/apiClient";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

type LoginUser = {
userType?: string;
company?: string;
[key: string]: any;
};

type LoginResponse = {
token?: string;
user?: LoginUser;
message?: string;
[key: string]: any;
};

export default function LoginForm() {
const { setAuth } = useAuth();
const navigate = useNavigate();

const [email, setEmail] = useState("");
const [password, setPassword] = useState("");
const [error, setError] = useState("");

const handleSubmit = async (e: React.FormEvent) => {
e.preventDefault();
setError("");

// Validation
if (!email.trim() || !password.trim()) {
  setError("Please enter both email and password.");
  return;
}

try {
  const response = (await AuthAPI.login(
    email.trim(),
    password
  )) as LoginResponse;

  console.log("🔐 Raw login response:", response);

  const user = response?.user ?? null;
  const token = response?.token ?? null;

  // Validate user
  if (!user) {
    throw new Error("Invalid user returned from server");
  }

  // Support both cookie auth and token auth
  setAuth(token, user);

  if (token) {
    localStorage.setItem("authToken", token);
  } else {
    localStorage.removeItem("authToken");
  }

  console.log("✅ Login successful");

  // Store role safely
  if (user.userType) {
    localStorage.setItem("userType", user.userType);
  }

  const role = user.userType?.toLowerCase();

  // Redirect
  if (role === "superadmin" || role === "admin") {
    navigate("/");
  } else {
    navigate("/");
  }
} catch (err: any) {
  console.error("❌ Login failed:", err);

  let message = "Login failed";

  try {
    const parsed = JSON.parse(err.message);
    message = parsed.message || message;
  } catch {
    message = err.message || message;
  }

  setError(message);
}

};

return (

/* <div className="min-h-screen bg-slate-100 flex items-start lg:items-center justify-center p-3 sm:p-4 lg:p-6 overflow-y-auto"> */

/* <div className="h-screen bg-slate-100 flex items-start lg:items-center justify-center p-3 sm:p-4 lg:p-6 overflow-y-auto"> */
<div className="min-h-[100dvh] bg-slate-100 flex items-center justify-center p-3 sm:p-4 lg:p-6 overflow-y-auto">
{/* Main Login Container */}
  <div className="w-full max-w-6xl bg-white rounded-2xl lg:rounded-3xl shadow-xl overflow-hidden flex flex-col lg:flex-row">

    {/* =====================================================
        LEFT SIDE
        Hidden on mobile
    ====================================================== */}
    <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-gradient-to-br from-green-50 via-white to-emerald-50">

      {/* Decorative background circles */}
      <div className="absolute -top-32 -left-32 w-80 h-80 rounded-full bg-green-100/70" />

      <div className="absolute -bottom-40 -right-32 w-[450px] h-[450px] rounded-full bg-green-100/60" />

      <div className="relative z-10 w-full p-7 xl:p-9 flex flex-col">

        {/* =================================================
            BRAND
        ================================================== */}
        <div className="flex items-center gap-4 mb-8">

          {/* LiveBaevr */}
          <div>
            <div className="flex items-center">
              <span className="text-4xl font-bold tracking-tight text-slate-900">
                Live
              </span>

              <span className="text-4xl font-bold tracking-tight text-green-600">
                Baevr
              </span>
            </div>

            <div className="text-sm text-slate-500 mt-1">
              Construction Project Management
            </div>
          </div>

          {/* Divider */}
          <div className="h-14 w-px bg-slate-300" />

          {/* nsite */}
          <div>
            <div className="text-4xl font-semibold tracking-tight text-slate-900">
              n<span className="text-green-600">site</span>
            </div>

            <div className="text-sm text-slate-500 mt-1">
              Site Work Made Simple
            </div>
          </div>

        </div>

        {/* =================================================
            MAIN HEADING
        ================================================== */}
        <div className="max-w-2xl">

          <h1 className="text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-[1.05] text-slate-900">
            Plan.
            <br />

            <span className="text-green-600">
              Track.
            </span>

            <br />

            Build.
            <span className="text-green-600">
              {" "}Together.
            </span>
          </h1>

          <p className="mt-7 text-lg leading-relaxed text-slate-500 max-w-xl">
            Track construction projects, manage tasks, record daily
            site work and keep your entire project team updated
            in one place.
          </p>

        </div>

        {/* =================================================
            FEATURES
        ================================================== */}
        <div className="grid grid-cols-2 gap-4 mt-7 max-w-2xl">

          {/* Task Tracking */}
          <div
            className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-100 shadow-sm"
          >

            <div className="w-10 h-10 shrink-0 rounded-xl bg-green-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
            </div>

            <div>
              <h3 className="font-semibold text-slate-800">
                Task Tracking
              </h3>

              <p className="text-sm text-slate-500 mt-1">
                Stay on schedule
              </p>
            </div>

          </div>

          {/* Project Updates */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-100 shadow-sm">

            <div className="w-10 h-10 shrink-0 rounded-xl bg-blue-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 19h16M6 17V9m4 8V5m4 12v-6m4 6V7"
                />
              </svg>
            </div>

            <div>
              <h3 className="font-semibold text-slate-800">
                Project Updates
              </h3>

              <p className="text-sm text-slate-500 mt-1">
                Monitor progress
              </p>
            </div>

          </div>

          {/* Site Daily Work */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-100 shadow-sm">

            <div className="w-10 h-10 shrink-0 rounded-xl bg-orange-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z"
                />
              </svg>
            </div>

            <div>
              <h3 className="font-semibold text-slate-800">
                Site Daily Work
              </h3>

              <p className="text-sm text-slate-500 mt-1">
                Log site activities
              </p>
            </div>

          </div>

          {/* Daily Reports */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-100 shadow-sm">

            <div className="w-10 h-10 shrink-0 rounded-xl bg-purple-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h6l5 5v11a2 2 0 01-2 2z"
                />
              </svg>
            </div>

            <div>
              <h3 className="font-semibold text-slate-800">
                Daily Reports
              </h3>

              <p className="text-sm text-slate-500 mt-1">
                Record site work
              </p>
            </div>

          </div>

        </div>

        {/* =================================================
            BOTTOM MESSAGE
        ================================================== */}
        <div className="mt-auto pt-6">

          <div className="flex items-center gap-3">

            <div className="h-10 w-1 rounded-full bg-green-500" />

            <div>
              <p className="text-sm font-semibold text-slate-700">
                Better Projects. Better Visibility.
              </p>

              <p className="text-sm text-slate-400 mt-1">
                Everything your construction team needs.
              </p>
            </div>

          </div>

        </div>

      </div>
    </div>

    {/* =====================================================
        RIGHT SIDE - LOGIN
        This is the ONLY side shown on mobile
    ====================================================== */}
    <div className="w-full lg:w-[45%] flex items-center justify-center bg-white px-5 py-6 sm:px-8 sm:py-8 lg:p-10">

      <div className="w-full max-w-md">

        {/* Mobile Branding */}
        <div className="lg:hidden text-center mb-6">

          <div className="flex justify-center items-center gap-4">

            <div className="text-left">
              <div className="text-3xl font-bold text-slate-900">
                Live<span className="text-green-600">Baevr</span>
              </div>

              <div className="text-xs text-slate-400">
                Construction Management
              </div>
            </div>

            <div className="h-10 w-px bg-slate-300" />

            <div className="text-left">
              <div className="text-3xl font-semibold text-slate-900">
                n<span className="text-green-600">site</span>
              </div>

              <div className="text-xs text-slate-400">
                Site Work
              </div>
            </div>

          </div>

        </div>

        {/* =================================================
            LOGIN HEADING
        ================================================== */}
        <div className="mb-6">

          <h2 className="text-3xl font-bold text-slate-900">
            Welcome Back
          </h2>

          <p className="mt-2 text-slate-500">
            Sign in to continue to LiveBaevr
          </p>

        </div>

        {/* =================================================
            LOGIN FORM
        ================================================== */}
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Email */}
          <div>

            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Email Address
            </label>

            <div className="relative">

              <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">

                <svg
                  className="w-5 h-5 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>

              </div>

              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="w-full border border-slate-200 rounded-xl pl-12 pr-4 py-3 bg-slate-50 text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:outline-none transition"
                placeholder="Enter your email"
              />

            </div>

          </div>

          {/* Password */}
          <div>

            <div className="flex items-center justify-between mb-2">

              <label className="block text-sm font-semibold text-slate-700">
                Password
              </label>

              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm font-medium text-green-600 hover:text-green-700 hover:underline"
              >
                Forgot password?
              </button>

            </div>

            <div className="relative">

              <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">

                <svg
                  className="w-5 h-5 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-7a2 2 0 00-2-2H6a2 2 0 00-2 2v7a2 2 0 002 2zm10-11V7a4 4 0 10-8 0v3h8z"
                  />
                </svg>

              </div>

              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="w-full border border-slate-200 rounded-xl pl-12 pr-4 py-3 bg-slate-50 text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:outline-none transition"
                placeholder="Enter your password"
              />

            </div>

          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">

              <svg
                className="w-5 h-5 text-red-500 mt-0.5 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v4m0 4h.01M10.29 3.86l-7.1 12.28A2 2 0 004.92 19h14.16a2 2 0 001.73-2.86l-7.1-12.28a2 2 0 00-3.42 0z"
                />
              </svg>

              <div className="text-red-600 text-sm font-medium">
                {error}
              </div>

            </div>
          )}

          {/* Login Button */}
          <button
            type="submit"
            className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 text-white py-3 rounded-xl transition-all duration-200 font-semibold shadow-md hover:shadow-lg flex items-center justify-center gap-2"
          >
            <span>Sign In</span>

            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>

          </button>

        </form>

        {/* =================================================
            REGISTER
        ================================================== */}
        <div className="mt-6 text-center">

          <p className="text-sm text-slate-500">
            New to LiveBaevr?
          </p>

          <button
            type="button"
            onClick={() => navigate("/register")}
            className="mt-1 text-sm font-semibold text-green-600 hover:text-green-700 hover:underline"
          >
            Create your account
          </button>

        </div>

        {/* =================================================
            COMPANY REGISTER
        ================================================== */}
        <div className="mt-5 pt-5 border-t border-slate-100 text-center">

          <p className="text-xs text-slate-400 mb-2">
            Are you registering a company?
          </p>

          <button
            type="button"
            onClick={() => navigate("/register-company")}
            className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline"
          >
            Register a Company
          </button>

        </div>

        {/* =================================================
            FOOTER
        ================================================== */}
        <div className="mt-5 text-center">

          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} Agile Civil Engineers Pvt. Ltd
          </p>

        </div>

      </div>
    </div>

  </div>
</div>

);
}